"use client";

import {
  Command as CmdkCommand,
  CommandEmpty as CmdkCommandEmpty,
  CommandGroup as CmdkCommandGroup,
  CommandInput as CmdkCommandInput,
  CommandItem as CmdkCommandItem,
  CommandList as CmdkCommandList,
  CommandSeparator as CmdkCommandSeparator,
} from "cmdk";
import { forwardRef } from "react";

import { Dialog, DialogContent, type DialogProps } from "#ui/dialog";

import {
  commandDialogCommandVariants,
  commandEmptyVariants,
  commandGroupVariants,
  commandInputWrapperVariants,
  commandItemVariants,
  commandListVariants,
  commandSeparatorVariants,
  commandShortcutVariants,
  commandVariants,
  type CommandEmptyVariants,
  type CommandGroupVariants,
  type CommandInputWrapperVariants,
  type CommandItemVariants,
  type CommandListVariants,
  type CommandSeparatorVariants,
  type CommandShortcutVariants,
  type CommandVariants,
} from "./command.styles";
import { SearchInput } from "./search-input";

// -----------------------------------------------------------------------------

export type CommandRef = React.ComponentRef<typeof CmdkCommand>;

export interface CommandProps
  extends React.ComponentPropsWithoutRef<typeof CmdkCommand>, CommandVariants {}

const Command = forwardRef<CommandRef, CommandProps>(
  ({ className, ...props }, ref) => (
    <CmdkCommand
      {...props}
      className={commandVariants({ className })}
      ref={ref}
    />
  ),
);

Command.displayName = "Command";

export { Command };

// -----------------------------------------------------------------------------

export type CommandDialogRef = React.ComponentRef<typeof CmdkCommand>;

// Unused: CommandDialogCommandVariants props
export interface CommandDialogProps extends DialogProps {
  children: React.ReactNode;
  className?: string;
}

const CommandDialog = forwardRef<CommandDialogRef, CommandDialogProps>(
  ({ children, className, ...props }, ref) => (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0">
        <Command
          className={commandDialogCommandVariants({ className })}
          ref={ref}
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  ),
);

CommandDialog.displayName = "CommandDialog";

export { CommandDialog };

// -----------------------------------------------------------------------------

export type CommandInputRef = React.ComponentRef<typeof CmdkCommandInput>;

export interface CommandInputProps
  extends
    Omit<React.ComponentPropsWithoutRef<typeof CmdkCommandInput>, "size">,
    CommandInputWrapperVariants {}

const CommandInput = forwardRef<CommandInputRef, CommandInputProps>(
  ({ className, ...props }, ref) => (
    <div
      className={commandInputWrapperVariants({ className })}
      data-slot="cmdk-input-wrapper"
    >
      <CmdkCommandInput {...props} ref={ref} asChild>
        <SearchInput name="cmdk-input" />
      </CmdkCommandInput>
    </div>
  ),
);

CommandInput.displayName = "CommandInput";

export { CommandInput };

// -----------------------------------------------------------------------------

export type CommandListRef = React.ComponentRef<typeof CmdkCommandList>;

export interface CommandListProps
  extends
    React.ComponentPropsWithoutRef<typeof CmdkCommandList>,
    CommandListVariants {}

const CommandList = forwardRef<CommandListRef, CommandListProps>(
  ({ className, ...props }, ref) => (
    <CmdkCommandList
      {...props}
      className={commandListVariants({ className })}
      ref={ref}
    />
  ),
);

CommandList.displayName = "CommandList";

export { CommandList };

// -----------------------------------------------------------------------------

export type CommandEmptyRef = React.ComponentRef<typeof CmdkCommandEmpty>;

export interface CommandEmptyProps
  extends
    React.ComponentPropsWithoutRef<typeof CmdkCommandEmpty>,
    CommandEmptyVariants {}

const CommandEmpty = forwardRef<CommandEmptyRef, CommandEmptyProps>(
  ({ className, ...props }, ref) => (
    <CmdkCommandEmpty
      {...props}
      className={commandEmptyVariants({ className })}
      ref={ref}
    />
  ),
);

CommandEmpty.displayName = "CommandEmpty";

export { CommandEmpty };

// -----------------------------------------------------------------------------

export type CommandGroupRef = React.ComponentRef<typeof CmdkCommandGroup>;

export interface CommandGroupProps
  extends
    React.ComponentPropsWithoutRef<typeof CmdkCommandGroup>,
    CommandGroupVariants {}

const CommandGroup = forwardRef<CommandGroupRef, CommandGroupProps>(
  ({ className, ...props }, ref) => (
    <CmdkCommandGroup
      {...props}
      className={commandGroupVariants({ className })}
      ref={ref}
    />
  ),
);

CommandGroup.displayName = "CommandGroup";

export { CommandGroup };

// -----------------------------------------------------------------------------

export type CommandSeparatorRef = React.ComponentRef<
  typeof CmdkCommandSeparator
>;

export interface CommandSeparatorProps
  extends
    React.ComponentPropsWithoutRef<typeof CmdkCommandSeparator>,
    CommandSeparatorVariants {}

const CommandSeparator = forwardRef<CommandSeparatorRef, CommandSeparatorProps>(
  ({ className, ...props }, ref) => (
    <CmdkCommandSeparator
      {...props}
      className={commandSeparatorVariants({ className })}
      ref={ref}
    />
  ),
);

CommandSeparator.displayName = "CommandSeparator";

export { CommandSeparator };

// -----------------------------------------------------------------------------

export type CommandItemRef = React.ComponentRef<typeof CmdkCommandItem>;

export interface CommandItemProps
  extends
    React.ComponentPropsWithoutRef<typeof CmdkCommandItem>,
    CommandItemVariants {}

const CommandItem = forwardRef<CommandItemRef, CommandItemProps>(
  ({ className, ...props }, ref) => (
    <CmdkCommandItem
      {...props}
      className={commandItemVariants({ className })}
      ref={ref}
    />
  ),
);

CommandItem.displayName = "CommandItem";

export { CommandItem };

// -----------------------------------------------------------------------------

export type CommandShortcutRef = React.ComponentRef<"span">;

export interface CommandShortcutProps
  extends React.ComponentPropsWithoutRef<"span">, CommandShortcutVariants {}

const CommandShortcut = forwardRef<CommandShortcutRef, CommandShortcutProps>(
  ({ className, ...props }, ref) => (
    <span
      {...props}
      className={commandShortcutVariants({ className })}
      ref={ref}
    />
  ),
);

CommandShortcut.displayName = "CommandShortcut";

export { CommandShortcut };
